import React, { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, BookOpen, Loader2, Quote, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useLenis } from "@/hooks/useLenis";
import api from "@/lib/api";

const SUGGESTIONS = [
  "John 3:16",
  "Psalm 23",
  "Grace",
  "Sabbath rest",
  "Romans 8:28",
  "Hope",
];

/**
 * Masked line-by-line reveal — signature on-load moment
 */
const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className={`inline-block overflow-hidden align-bottom ${className}`}>
    <motion.span
      className="inline-block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

/**
 * Slow editorial marquee — one continuous horizontal drift
 */
const Marquee = ({ items }) => (
  <div className="relative overflow-hidden py-3 border-y border-white/10 bg-[#003B5C]" aria-hidden="true">
    <motion.div
      className="flex whitespace-nowrap gap-16 font-display text-white/40"
      style={{ fontSize: "clamp(3rem, 7vw, 6rem)", fontWeight: 300, letterSpacing: "-0.03em" }}
      animate={{ x: ["0%", "-50%"] }}
      transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
    >
      {[...items, ...items].map((t, i) => (
        <span key={`${t}-${i < items.length ? "a" : "b"}`} className="flex items-center gap-16">
          {t}
          <span className="text-[#D4A437]">✦</span>
        </span>
      ))}
    </motion.div>
  </div>
);

/**
 * Numbered chapter heading
 */
const Chapter = ({ num, title, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="mb-14 flex items-baseline gap-6"
  >
    <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4A437]">{num}</span>
    <div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#003B5C] leading-[1.05]">
        {title}
      </h2>
      {subtitle && <p className="mt-3 text-sm text-[#1E1E1E]/60 max-w-md">{subtitle}</p>}
    </div>
  </motion.div>
);

export default function BibleExplorer() {
  useLenis();
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 100]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const submit = async (e, presetQuery) => {
    if (e) e.preventDefault();
    const q = (presetQuery ?? query).trim();
    if (!q) {
      toast.error("Please enter a verse or topic.");
      return;
    }
    if (presetQuery) setQuery(presetQuery);
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/bible/explore", { query: q });
      setResult(data);
      // Scroll to result
      setTimeout(() => {
        document.getElementById("explorer-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="bible-explorer-page" className="bg-white">
      {/* HERO — kinetic masked reveal */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden bg-[#003B5C] grain-overlay"
      >
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-32 w-[560px] h-[560px] rounded-full bg-[#D4A437]/10 blur-3xl" />
          <div className="absolute -bottom-52 -left-40 w-[720px] h-[720px] rounded-full bg-[#D4A437]/6 blur-3xl" />
          <div className="absolute inset-0" style={{
            backgroundImage: "repeating-linear-gradient(135deg, rgba(255,255,255,0.02) 0 1px, transparent 1px 100px)",
          }} />
        </motion.div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32 lg:py-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md border border-white/15 rounded-full text-[10px] uppercase tracking-[0.3em] text-[#D4A437] font-bold mb-10"
          >
            <Sparkles size={12} />
            Bible Companion · AI Powered
          </motion.div>

          <h1 className="font-display font-black tracking-[-0.03em] leading-[0.9] text-white" style={{ fontSize: "clamp(3rem, 9vw, 8.5rem)" }}>
            <div><MaskLine delay={0.05}>Read.</MaskLine></div>
            <div><MaskLine delay={0.2}>Reflect.</MaskLine></div>
            <div><MaskLine delay={0.35}><span className="text-[#D4A437] italic font-light">Rest in Him.</span></MaskLine></div>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 text-lg sm:text-xl text-white/70 max-w-xl leading-relaxed"
          >
            Enter a verse, a passage, or a single word. Receive a warm, Christ-centered reflection
            grounded in Scripture — a moment of quiet in the middle of your day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-14 max-w-2xl"
          >
            <form onSubmit={submit} data-testid="explorer-form">
              <div className="relative group">
                <div className="absolute inset-0 -m-px rounded-sm bg-gradient-to-r from-[#D4A437]/60 via-[#D4A437]/20 to-[#D4A437]/60 opacity-0 group-focus-within:opacity-100 transition-opacity blur-sm" />
                <div className="relative flex items-center bg-white/5 backdrop-blur-xl border border-white/20 rounded-sm focus-within:border-[#D4A437]/60 transition-colors">
                  <BookOpen size={20} className="ml-5 text-[#D4A437] flex-shrink-0" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="John 3:16, Psalm 23, grace, hope…"
                    data-testid="explorer-input"
                    className="flex-1 bg-transparent px-5 py-5 text-white text-lg placeholder:text-white/40 focus:outline-none"
                    maxLength={200}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="explorer-submit"
                    className="mr-2 inline-flex items-center gap-2 bg-[#D4A437] hover:bg-[#B88C2A] disabled:opacity-60 text-white px-6 py-3 rounded-sm text-xs font-bold uppercase tracking-[0.2em] transition-all"
                  >
                    {loading ? (<><Loader2 size={14} className="animate-spin" /> Praying</>) : (<>Explore <ArrowRight size={14} /></>)}
                  </button>
                </div>
              </div>
            </form>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-xs uppercase tracking-[0.2em] text-white/40 mr-2 self-center">Try:</span>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => submit(null, s)}
                  data-testid={`suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  disabled={loading}
                  className="px-3.5 py-1.5 text-xs text-white/70 hover:text-white border border-white/15 hover:border-[#D4A437]/60 rounded-full transition-all disabled:opacity-40"
                >
                  {s}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-[10px] uppercase tracking-[0.3em] flex flex-col items-center gap-2"
        >
          <span>Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent"
          />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee items={["Scripture", "Reflection", "Prayer", "Grace", "Hope", "Sabbath"]} />

      {/* RESULT */}
      <AnimatePresence mode="wait">
        {result && (
          <motion.section
            id="explorer-result"
            key={result.query}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="py-24 lg:py-32 bg-white"
            data-testid="explorer-result"
          >
            <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
              {/* Chapter 01 — The verse */}
              {result.verse_text && (
                <div className="mb-24">
                  <Chapter num="01 / Scripture" title="The Word." subtitle={result.verse_reference} />
                  <motion.figure
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                  >
                    <Quote size={44} className="text-[#D4A437] mb-6" />
                    <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl leading-[1.35] text-[#003B5C] tracking-tight" style={{ fontWeight: 500 }}>
                      "{result.verse_text}"
                    </blockquote>
                    {result.verse_reference && (
                      <figcaption className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-[#D4A437]">
                        — {result.verse_reference}
                      </figcaption>
                    )}
                  </motion.figure>
                </div>
              )}

              {/* Chapter 02 — Summary */}
              {result.summary && (
                <div className="mb-24">
                  <Chapter num="02 / Summary" title="At a glance." />
                  <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="text-xl sm:text-2xl leading-[1.55] text-[#1E1E1E]/80 max-w-3xl font-light"
                  >
                    {result.summary}
                  </motion.p>
                </div>
              )}

              {/* Chapter 03 — Themes */}
              {result.key_themes && result.key_themes.length > 0 && (
                <div className="mb-24">
                  <Chapter num="03 / Themes" title="Key threads." />
                  <div className="flex flex-wrap gap-3">
                    {result.key_themes.map((t, i) => (
                      <motion.span
                        key={t + i}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        data-testid={`theme-${i}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#F7F8FA] border border-gray-200 rounded-full text-sm font-medium text-[#003B5C]"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4A437]" />
                        {t}
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}

              {/* Chapter 04 — Reflection */}
              {result.reflection && (
                <div className="mb-24">
                  <Chapter num="04 / Reflection" title="A moment to sit with." />
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="relative bg-[#003B5C] text-white p-10 sm:p-14 lg:p-16 rounded-sm grain-overlay overflow-hidden"
                  >
                    <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#D4A437]/10 blur-2xl" />
                    <p className="relative text-lg sm:text-xl lg:text-2xl leading-[1.65] font-light">
                      {(result.reflection || "").replace(/^\s*(AI[\s-]*(generated)?[\s-]*explanation)\s*[:—-]\s*/i, "")}
                    </p>
                  </motion.div>
                </div>
              )}

              {/* Chapter 05 — Cross references */}
              {result.cross_references && result.cross_references.length > 0 && (
                <div>
                  <Chapter num="05 / Go Deeper" title="Continue the journey." subtitle="Scripture speaks best in conversation with itself. Explore these companion passages." />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {result.cross_references.map((ref, i) => (
                      <motion.button
                        key={ref + i}
                        onClick={() => submit(null, ref)}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        data-testid={`cross-ref-${i}`}
                        className="group text-left p-6 border border-gray-200 hover:border-[#D4A437] rounded-sm transition-all hover:-translate-y-1 hover:shadow-md"
                      >
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437] font-bold mb-2">Read</div>
                        <div className="font-display text-xl font-bold text-[#003B5C] group-hover:underline">{ref}</div>
                        <ArrowRight size={16} className="text-[#003B5C] mt-4 group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chapter 06 — Official Adventist Resources */}
              {result.official_citations && result.official_citations.length > 0 && (
                <div className="mt-24">
                  <Chapter num="06 / Doctrine" title="Official Adventist Resources." subtitle="Reviewed summaries drawn from the General Conference 28 Fundamental Beliefs. Read the full official statements at adventist.org." />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.official_citations.map((c, i) => (
                      <motion.a
                        key={c.belief_number}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        data-testid={`official-citation-${c.belief_number}`}
                        className="group block p-6 bg-[#F7F8FA] border border-gray-200 hover:border-[#003B5C] rounded-sm transition-all hover:-translate-y-1"
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437] font-bold">
                            Fundamental Belief #{c.belief_number}
                          </div>
                          <ExternalLink size={14} className="text-[#003B5C]/60 group-hover:text-[#003B5C] flex-shrink-0" />
                        </div>
                        <h3 className="font-display text-lg font-bold text-[#003B5C] mb-2 group-hover:underline">{c.title}</h3>
                        <p className="text-sm text-[#1E1E1E]/70 leading-relaxed mb-4">{c.summary}</p>
                        <div className="flex items-center gap-2 text-[10px] text-[#1E1E1E]/50 font-mono uppercase tracking-wider">
                          <span>{c.source_domain}</span>
                          <span>·</span>
                          <span>Reviewed {c.date_last_reviewed}</span>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </div>
              )}

              {/* Chapter 07 — Optional EGW Further Reading */}
              {result.egw_further_reading && result.egw_further_reading.length > 0 && (
                <div className="mt-24">
                  <Chapter num="07 / Further Reading" title="Ellen G. White writings." subtitle="Optional external study links. These are search results — not verified direct quotations." />
                  <div className="grid grid-cols-1 gap-3">
                    {result.egw_further_reading.map((link, i) => (
                      <motion.a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        data-testid={`egw-link-${i}`}
                        className="group flex items-center justify-between gap-4 p-5 border border-gray-200 hover:border-[#D4A437] rounded-sm transition-all"
                      >
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A437] font-bold mb-1">External Study Link</div>
                          <div className="font-display text-base font-bold text-[#003B5C] group-hover:underline">{link.label}</div>
                          <div className="text-xs text-[#1E1E1E]/55 mt-1 italic">{link.note}</div>
                        </div>
                        <ExternalLink size={16} className="text-[#003B5C]/70 flex-shrink-0" />
                      </motion.a>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div data-testid="explore-disclaimer" className="mt-20 pt-8 border-t border-gray-100">
                <p className="text-xs text-[#1E1E1E]/55 leading-relaxed max-w-3xl">
                  <span className="font-semibold text-[#003B5C]">{result.ai_label || "AI-generated explanation."}</span>
                  {" "}This local church study tool uses selected official Seventh-day Adventist sources for educational purposes. It is not operated by or an official publication of the General Conference of Seventh-day Adventists.
                </p>
              </div>

              {/* Ask another */}
              <div className="mt-24 pt-16 border-t border-gray-100 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4A437] mb-6">Continue exploring</p>
                <button
                  onClick={() => { setResult(null); setQuery(""); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid="explorer-reset"
                  className="inline-flex items-center gap-2 border-2 border-[#003B5C] text-[#003B5C] px-8 py-4 rounded-sm text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#003B5C] hover:text-white transition-all"
                >
                  Ask another
                </button>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* MANIFESTO */}
      {!result && (
        <section className="py-32 lg:py-40 bg-white">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
            <Chapter num="01 / Purpose" title="Why we made this." />
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="space-y-6 text-lg sm:text-xl leading-[1.7] text-[#1E1E1E]/75 max-w-3xl"
            >
              <p>
                The Bible is not a textbook to conquer. It's a conversation to enter. We built this
                companion to offer a doorway — a warm invitation for anyone, wherever you are on
                your journey, to <span className="text-[#003B5C] font-semibold">meet Jesus in Scripture</span>.
              </p>
              <p>
                Type a verse you love. Or a word you're wrestling with. Or nothing at all — and just
                click one of the suggestions above.
              </p>
              <p className="font-display text-2xl sm:text-3xl text-[#003B5C] pt-4">
                Then simply <span className="italic text-[#D4A437]">rest</span> in what He speaks.
              </p>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}
